import type { Options } from 'highcharts';
import { z } from 'zod';
import { ScanAggregatesSchema } from '@contract/scanRun';
import { chartTokens } from '@contract/chartTokens';
import { seriesPalette, tokenFor, type ThemeMode } from '@utils/chartTokens';

/**
 * REV-711: the three panels across the top of a scan run — WCAG level, success
 * criteria failed, tests failed.
 *
 * Built here rather than inline in the page so the truncation and colouring
 * rules are testable without rendering, and so the page stays a layout.
 *
 * The type is INFERRED from the vendored contract rather than hand-written.
 * `testService.ScanRunDetail` is hand-typed and predates the contract; adding a
 * second hand-typed copy of a shape the backend owns is the REV-345 drift the
 * contract exists to stop.
 */
export type ScanAggregates = z.infer<typeof ScanAggregatesSchema>;

/**
 * How many bars a breakdown chart draws.
 *
 * Ten is a legibility limit, not a data limit: past it the labels collide and
 * the chart stops being readable. The data table beside it always lists
 * everything, and the panel says "top 10 of 34" when it truncates — a chart
 * that quietly showed a tenth of the data would be the same under-reporting
 * the API's `unspecified` bucket exists to prevent, one layer up.
 */
export const CHART_TOP_N = 10;

export interface PanelLabels {
  level: string;
  criteria: string;
  tests: string;
  findings: string;
  criterion: string;
  rule: string;
  showTable: string;
  topOf: (shown: number, total: number) => string;
  unspecified: string;
}

export interface ChartPanel {
  title: string;
  description: string;
  /** Null when there is nothing to draw. */
  options: Options | null;
  /**
   * False when this panel has no data at all.
   *
   * The page uses it to render a short "nothing here" line instead of a table
   * with headers and no rows. That is not a hypothetical: a scan whose
   * findings all come from rules the catalog has no criteria for populates the
   * level panel and leaves these two empty, and 42 of the catalog's 292 rules
   * are exactly that.
   */
  hasData: boolean;
  tableLabel: string;
  columns: string[];
  rows: Array<{ cells: string[]; to?: string }>;
}

const BAR_HEIGHT = 260;

/** Findings per WCAG level, coloured from the shared token table. */
export function wcagLevelPanel(
  aggregates: ScanAggregates,
  mode: ThemeMode,
  labels: PanelLabels
): ChartPanel {
  const buckets: Array<{ key: keyof ScanAggregates['byWcagLevel']; label: string; fill: string }> =
    [
      { key: 'A', label: 'A', fill: tokenFor('wcagLevel', 'A', mode).fill },
      { key: 'AA', label: 'AA', fill: tokenFor('wcagLevel', 'AA', mode).fill },
      { key: 'AAA', label: 'AAA', fill: tokenFor('wcagLevel', 'AAA', mode).fill },
      // Not a level, so it does not get a level's colour. It is the bucket for a
      // rule the catalog has no criteria for, or none at all.
      {
        key: 'unspecified',
        label: labels.unspecified,
        fill: chartTokens.neutral.unknown[mode].fill,
      },
    ];

  const data = buckets.map((b) => ({ y: aggregates.byWcagLevel[b.key], color: b.fill }));
  const total = data.reduce((n, d) => n + d.y, 0);

  return {
    title: labels.level,
    description: labels.level,
    hasData: total > 0,
    options:
      total === 0
        ? null
        : {
            chart: { type: 'column', height: BAR_HEIGHT },
            title: { text: undefined },
            xAxis: { categories: buckets.map((b) => b.label), title: { text: null } },
            yAxis: { min: 0, allowDecimals: false, title: { text: labels.findings } },
            legend: { enabled: false },
            series: [{ type: 'column', name: labels.findings, colorByPoint: true, data }],
          },
    tableLabel: labels.showTable,
    columns: [labels.level, labels.findings],
    // Every level, including the zeroes: "AA: 0" says the scan looked and found
    // nothing, which is not the same as AA being absent from the report.
    rows: buckets.map((b) => ({
      cells: [b.label, String(aggregates.byWcagLevel[b.key])],
    })),
  };
}

/** Shared shape for the two "top N by findings" breakdowns. */
function breakdownPanel(
  entries: Array<{ label: string; findings: number }>,
  colour: string,
  labels: PanelLabels,
  title: string,
  keyColumn: string
): ChartPanel {
  const ranked = [...entries].sort((a, b) => b.findings - a.findings);
  const shown = ranked.slice(0, CHART_TOP_N);
  const truncated = ranked.length > CHART_TOP_N;

  return {
    title,
    description: truncated ? `${title} — ${labels.topOf(shown.length, ranked.length)}` : title,
    hasData: ranked.length > 0,
    options: ranked.length
      ? {
          chart: { type: 'bar', height: Math.max(BAR_HEIGHT, shown.length * 28 + 60) },
          title: { text: undefined },
          xAxis: {
            categories: shown.map((e) => e.label),
            title: { text: null },
            // REV-939: Highcharts ellipsises a category label that outgrows its
            // share of the width, and rule ids share long prefixes — three bars
            // all read "NON-TEXT-…". A label is the bar's only name, so it is
            // drawn whole, on one line, and the plot gives up the width.
            labels: { style: { textOverflow: 'none', whiteSpace: 'nowrap' } },
          },
          yAxis: { min: 0, allowDecimals: false, title: { text: labels.findings } },
          legend: { enabled: false },
          series: [
            {
              type: 'bar',
              name: labels.findings,
              color: colour,
              data: shown.map((e) => e.findings),
            },
          ],
        }
      : null,
    tableLabel: labels.showTable,
    columns: [keyColumn, labels.findings],
    // All of them. The chart is the summary; the table is the record.
    rows: ranked.map((e) => ({ cells: [e.label, String(e.findings)] })),
  };
}

/** Success criteria failed, heaviest first. */
export function criteriaPanel(
  aggregates: ScanAggregates,
  mode: ThemeMode,
  labels: PanelLabels
): ChartPanel {
  return breakdownPanel(
    aggregates.criteriaFailed.byCriterion.map((c) => ({ label: c.id, findings: c.findings })),
    seriesPalette(mode)[2] as string,
    labels,
    labels.criteria,
    labels.criterion
  );
}

/** Tests (rules) failed, heaviest first. */
export function testsPanel(
  aggregates: ScanAggregates,
  mode: ThemeMode,
  labels: PanelLabels
): ChartPanel {
  return breakdownPanel(
    aggregates.testsFailed.byRule.map((r) => ({ label: r.ruleId, findings: r.findings })),
    seriesPalette(mode)[1] as string,
    labels,
    labels.tests,
    labels.rule
  );
}
